import React, { useRef, useEffect } from 'react';
import googleMaps from '../../../services/maps/google';
import appleMaps from '../../../services/maps/mapKitJs';
import getIcons from '../../../services/maps/shared/markerIcons';
import toSnakeCase from '../../../helpers/to_snake_case';
import debounce from '../../../helpers/debounce';
import { createDiv } from '../../../helpers/htmlBuilder';
import '../../../../sass/pages/list/offered/map/info_window.scss';

// amount to increase the visible map by to allow for a search margin.
const mapMargin = 1.1;

let mapInstance;
let maps;
let map;
let markerIcons;

function getMarkerIcon(context, newness) {
  if (context) {
    return markerIcons[context];
  }
  if (newness) {
    return markerIcons[toSnakeCase(newness)];
  }
  return markerIcons.default;
}

const imageContent = (img_url, advert_id) => {
  if (img_url) {
    return `<a href="/${advert_id}" class="info-content__image-wrap">
      <img class="info-content__image" src="${img_url}" alt="">
    </a>`;
  }
  return '';
};

const letAgreed = (status, localisations) => {
  if (status == 'Not available') {
    return ` <div class="info-content__now-let status">${localisations.let_agreed}</div>`;
  }
  return '';
};

function calloutContent(
  {
    advert_id,
    accom_type,
    main_image_square_url,
    ad_title,
    price,
    ad_text_255,
    listing_url,
    status,
  },
  localisations,
) {
  return `
  <div class="info-content__wrapper">
    <div class="info-content__header">
      ${imageContent(main_image_square_url, advert_id)}
      <dl class="info-content__list">
        <dt class="info-content__term">ad #ref</dt>
        <dd class="info-content__description">#${advert_id}</dd>
        <dt class="info-content__term">Title</dt>
        <dd class="info-content__description">
          <a href="${listing_url}">${ad_title}</a></a>
        </dd>
        <dt class="info-content__term">Accom type</dt>
        <dd class="info-content__description">${accom_type}</dd>
        <dt class="info-content__term">Price<dt>
        <dd class="info-content__description">${price}</dd>
      </dl>
    </div>
    <p class="info-content__body">${ad_text_255}</p>
    ${letAgreed(status, localisations)}
 </div>
  `;
}

function Map({
  base_url,
  domain,
  location: { latitude, latitude_delta, longitude, longitude_delta },
  results,
  previousResults,
  staticFilesServer,
  updateLocation,
  updateMapLoaded,
  useAppleMaps,
  appleMapsToken,
  isLoading,
  country,
  localisations,
}) {
  const mapRef = useRef(null);
  function regionChanged({
    latitude: lat,
    longitude: long,
    latitudeDelta,
    longitudeDelta,
  }) {
    updateLocation({
      latitude: lat,
      latitude_delta: latitudeDelta / mapMargin,
      longitude: long,
      longitude_delta: longitudeDelta / mapMargin,
    });
  }
  const debouncedRegionChanged = debounce(regionChanged, 200);

  async function loadMaps() {
    maps = useAppleMaps ? appleMaps : googleMaps;
    mapInstance = await maps.load({ domain, base_url, JWT: appleMapsToken });

    // If only a single result on map to show
    if (!latitude_delta && !longitude_delta) {
      map = maps.createFromCenter(mapInstance, {
        mountElement: mapRef.current,
        latitude,
        longitude,
      });
    }

    if (latitude_delta && longitude_delta) {
      map = maps.createFromRegion(mapInstance, {
        mountElement: mapRef.current,
        latitude,
        longitude,
        latitudeDelta: latitude_delta * mapMargin,
        longitudeDelta: longitude_delta * mapMargin,
      });
    }

    maps.onRegionChanged(mapInstance, {
      map,
      callback: debouncedRegionChanged,
    });
    updateMapLoaded(true);
  }

  function applyMarker(result) {
    const div = createDiv({
      class: 'info-content',
    });
    const content = calloutContent(result, localisations);
    div.innerHTML = content;
    const markerIcon = getMarkerIcon(result.context, result.new);
    return maps.addMarker(mapInstance, {
      map,
      latitude: result.latitude,
      longitude: result.longitude,
      infoWindowHtml: div,
      markerId: result.advert_id,
      markerIcon,
    });
  }

  function updateMap() {
    if (map) {
      Object.keys(map.markers).forEach((markerId) => {
        maps.removeMarker(mapInstance, {
          map,
          markerId,
        });
      });
      results.forEach((result) => {
        applyMarker(result);
      });
    }
  }
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // probably better to pass these in as props
    markerIcons = getIcons(staticFilesServer, country);
    loadMaps();
  }, []);
  useEffect(() => {
    updateMap();
  }, [results]);

  /* eslint-enable react-hooks/exhaustive-deps */
  return (
    <div ref={mapRef} id="mount-search-map" aria-busy={isLoading}>
      {isLoading ? (
        <div className="search-map-is-loading">
          <i aria-hidden="true" className="far fa-spinner fa-pulse"></i>
        </div>
      ) : null}
    </div>
  );
}

export default Map;
